/**
 * Générateur — Automatisme : connaître et utiliser les formules du volume
 * d'un cube, d'un pavé, d'un prisme, d'un cylindre.
 * Attendu ID : 1117 | Niveau : 4ème | Domaine : Représentation de l'espace
 * Rubrique : AUTOMATISME (programme 2026, Espace 4e) — rituel court.
 * Labels (cartes exerciseur) :
 *   - La bonne formule               (QCM formule d'un solide ; relier solide coté ↔ calcul de son volume ; V/F de pur rappel)
 *   - Calculer le volume             (saisie sur figure cotée ; QCM à leurres de fausses formules)
 *   - Retrouver une dimension        (saisie : la hauteur connaissant le volume et la base)
 *   - Vrai ou faux ?                 (V/F sur des calculs)
 *
 * ── Plan de cartes validé (2026-09-09) ─────────────────────────────────────
 * Source : BO cycle 4 (2026), Représentation de l'espace 4e, automatisme
 * « Connaître et utiliser les formules du volume d'un cube, d'un pavé, d'un
 * prisme, d'un cylindre » + grille P1-P12. P1 : CM2 absent (contenances
 * seulement), 6e introduit (cm³, dénombrement de cubes), 5e formalisé
 * (volume du cube, du pavé droit, du prisme droit, du cylindre ; aire du
 * disque), 4e automatisme. P2 : sens installés — volume = nombre de cubes
 * unités ; L × l × h ; c³ ; aire de la base × hauteur ; π r² ; non installés
 * (distracteurs) — pyramide et cône (le tiers), aire latérale 2 π r h,
 * périmètre × hauteur, aire prise pour volume. P3 : arêtes entières ≤ 10,
 * cubes de 2, 3, 4, 5, 10 ; produits des tables (pavé L × l ≤ 30, aire de
 * base ≤ 20, rayon ≤ 4) ; cylindre en « k π », jamais de valeur approchée. P4 : substitution dans une formule.
 * P5 : perspective cavalière (moteur svgSolides du 5e). P6 : aucune
 * calculatrice, π symbolique. P7 : pas de valeur approchée, pas de
 * conversion. P8 : volume, base, hauteur, arête, rayon, cm³, π. P9 : tables,
 * carrés et cubes simples, aire du rectangle, du triangle, du disque. P10 :
 * constat. P11 : c × 3 pour c³ ; ÷ 2 du triangle oublié ; 2 π r pour π r² ;
 * hauteur du prisme confondue avec celle du triangle ; cm² pour cm³. P12 :
 * rémanence forte.
 * INVARIANTS :
 *   - toute figure est CONSTRUITE depuis les dimensions (cotes affichées =
 *     paramètres) et porte data-solide / data-dims pour les solveurs ;
 *   - tous les volumes en ENTIERS (coefficient entier devant π pour le
 *     cylindre) ; leurres calculés, distincts, positifs ; V/F : pas de
 *     crochets, mixité 1-3 faux, familles exclusives, fenêtre stricte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie, makeRelier,
    retirerCodesErreur,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { paveNomme, cylindreDebout, prismeTri } from '../../5eme/espace/svgSolides';

const ATTENDU_ID = 1117;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FORMULE = 'La bonne formule';
const L_CALCUL = 'Calculer le volume';
const L_DIMENSION = 'Retrouver une dimension';
const L_VF = 'Vrai ou faux ?';

const UNITES = ['cm', 'cm', 'cm', 'dm', 'm'];
const NOM = { cube: 'ce cube', pave: 'ce pavé', prisme: 'ce prisme droit', cylindre: 'ce cylindre' };

// ─── Formules (texte affiché, sans lettres inutiles : celles du solide) ────
const FORMULES = {
    cube: { juste: 'V = c × c × c', fausses: ['V = c × 3', 'V = 6 × c × c', 'V = c × c'] },
    pave: { juste: 'V = L × l × h', fausses: ['V = L + l + h', 'V = L × l', 'V = 2 × (L × l + l × h + L × h)'] },
    prisme: { juste: 'V = aire de la base × h', fausses: ['V = périmètre de la base × h', 'V = aire de la base × h ÷ 3', 'V = aire de la base + h'] },
    cylindre: { juste: 'V = π × r × r × h', fausses: ['V = 2 × π × r × h', 'V = π × r × h', 'V = π × r × r × h ÷ 3'] },
};
const SOLIDES = ['cube', 'pave', 'prisme', 'cylindre'];
const NOM_SOLIDE = { cube: 'un cube d\'arête c', pave: 'un pavé de dimensions L, l et h', prisme: 'un prisme droit de hauteur h', cylindre: 'un cylindre de rayon r et de hauteur h' };

// ─── Tirage des solides et de leurs dimensions (volumes entiers, ≤ 300) ────
const tirerSolide = (type = randomChoice(SOLIDES)) => {
    const u = randomChoice(UNITES);
    if (type === 'cube') { const c = randomChoice([2, 3, 4, 5, 10]); return { type, u, c, V: c * c * c }; }
    if (type === 'pave') {
        for (let essai = 0; essai < 100; essai++) {
            const L = randInt(2, 10); const l = randInt(2, 8); const h = randInt(2, 8);
            // Calcul mental (prof, 2026-09-09) : le produit des deux premières dimensions reste ≤ 30
            if (L * l > 30 || L * l * h > 300 || new Set([L, l, h]).size < 2) continue;
            return { type, u, L, l, h, V: L * l * h };
        }
        return { type, u, L: 5, l: 4, h: 3, V: 60 };
    }
    if (type === 'prisme') {
        if (Math.random() < 0.5) {
            const B = randomChoice([6, 8, 10, 12, 15, 20]); const h = randInt(2, 10);
            if (B * h <= 300) return { type, u, base: 'aire', B, h, V: B * h };
            return { type, u, base: 'aire', B: 12, h: 5, V: 60 };
        }
        for (let essai = 0; essai < 100; essai++) {
            const a = randomChoice([3, 4, 5, 6]); const b = randomChoice([3, 4, 5, 6]); const h = randInt(2, 10);
            if (a === b || (a * b) % 2 !== 0 || (a * b / 2) * h > 300) continue;
            return { type, u, base: 'triangle', a, b, B: (a * b) / 2, h, V: ((a * b) / 2) * h };
        }
        return { type, u, base: 'triangle', a: 3, b: 4, B: 6, h: 5, V: 30 };
    }
    // Calcul mental : rayon 1 à 4 ; pour r = 4 (r² = 16), hauteur ≤ 5 ou 10
    const r = randInt(1, 4); const h = r === 4 ? randomChoice([2, 3, 4, 5, 10]) : randInt(2, 10);
    return { type, u, r, h, k: r * r * h, V: r * r * h };            // V = k π
};

const volumeAffiche = (S) => (S.type === 'cylindre' ? `${S.k}π ${S.u}³` : `${S.V} ${S.u}³`);

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ──────────────
// Valeurs fausses nommées d'un volume (le nombre devant π pour le cylindre), celles des leurres du QCM qui sont une vraie erreur.
// Lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md) : un code par erreur et par solide, ici, dans CODES_FORMULE et
// dans POOL_RAPPEL (le cube garde aire_pour_volume ; les leurres du relier gardent leurs codes).
const erreursVolume = (S) => {
    if (S.type === 'cube') return [['cube_arete_fois_3', S.c * 3], ['aire_pour_volume', S.c * S.c], ['aire_pour_volume', 6 * S.c * S.c]];
    if (S.type === 'pave') return [['aire_de_base_pour_volume', S.L * S.l], ['addition_pour_produit', S.L + S.l + S.h], ['addition_pour_produit', S.L * S.l + S.h], ['tiers_abusif', S.V / 3]];
    if (S.type === 'prisme') {
        return S.base === 'aire'
            ? [['addition_pour_produit', S.B + S.h], ['tiers_abusif', S.V / 3]]
            : [['triangle_sans_le_demi', S.a * S.b * S.h], ['perimetre_de_base_pour_volume', (S.a + S.b) * S.h], ['aire_de_base_pour_volume', S.B], ['tiers_abusif', S.V / 3]];
    }
    return [['perimetre_de_base_pour_volume', 2 * S.r * S.h], ['exposant_ignore', S.r * S.h], ['tiers_abusif', S.k / 3]];
};
/** Les erreurs sûres : valeurs entières positives, ni la bonne réponse, ni portées par deux codes. */
const erreursSures = (paires, juste) => {
    const parValeur = [];
    paires.forEach(([code, v]) => {
        if (!Number.isInteger(v) || v <= 0 || v === juste) return;
        const deja = parValeur.find((p) => p.v === v);
        if (deja) deja.codes.add(code); else parValeur.push({ v, codes: new Set([code]) });
    });
    return parValeur.filter((p) => p.codes.size === 1).map((p) => ({ code: [...p.codes][0], v: p.v }));
};
// Formules fausses nommées (la bonne formule d'un solide demandée).
const CODES_FORMULE = {
    'V = c × 3': 'cube_arete_fois_3',
    'V = 6 × c × c': 'aire_pour_volume',
    'V = c × c': 'aire_pour_volume',
    'V = L + l + h': 'addition_pour_produit',
    'V = L × l': 'aire_de_base_pour_volume',
    'V = 2 × (L × l + l × h + L × h)': 'aire_de_base_pour_volume',
    'V = périmètre de la base × h': 'perimetre_de_base_pour_volume',
    'V = aire de la base × h ÷ 3': 'tiers_abusif',
    'V = aire de la base + h': 'addition_pour_produit',
    'V = 2 × π × r × h': 'perimetre_de_base_pour_volume',
    'V = π × r × h': 'exposant_ignore',
    'V = π × r × r × h ÷ 3': 'tiers_abusif',
};

// ─── Figures cotées (perspective cavalière, moteur svgSolides + prisme local) ─
const VB = { w: 230, h: 175 };
const dataAttrs = (S) => {
    const dims = S.type === 'cube' ? `c=${S.c}` : S.type === 'pave' ? `L=${S.L};l=${S.l};h=${S.h}` : S.type === 'cylindre' ? `r=${S.r};h=${S.h}`
        : (S.base === 'aire' ? `B=${S.B};h=${S.h}` : `a=${S.a};b=${S.b};h=${S.h}`);
    return `data-solide="${S.type}" data-unite="${S.u}" data-dims="${dims}"`;
};
const avecData = (svg, S) => svg.replace('<svg ', `<svg ${dataAttrs(S)} `);
/** Élargit le cadre (les cotes du moteur débordent à gauche quand le solide remplit la boîte). */
const cadreLarge = (svg, vb, marge = 26) => svg.replace(`viewBox="0 0 ${vb.w} ${vb.h}"`, `viewBox="-${marge} -6 ${vb.w + 2 * marge} ${vb.h + 12}"`)
    .replace(`width="${vb.w}" height="${vb.h}"`, `width="${vb.w + 2 * marge}" height="${vb.h + 12}"`);

const COTE = (x, y, txt, anchor = 'middle') => `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" font-family="sans-serif" font-size="12" text-anchor="${anchor}" fill="#b45309">${txt}</text>`;

/**
 * Prisme droit à base TRIANGLE RECTANGLE, dessiné localement (le prismeTri du 5e a un triangle
 * quelconque). Base = triangle avant, angle droit en bas à gauche, côtés a (horizontal) et b
 * (vertical) ; la hauteur du prisme est la profondeur (fuyante). Projection cavalière k = 0,5, 32°.
 */
const prismeRectangle = (S) => {
    const k = 0.5; const al = (32 * Math.PI) / 180; const m = Math.max(S.a, S.b);
    const A = (2.6 * S.a) / m; const B = (2.6 * S.b) / m; const P = 1.6 + Math.min(S.h, 10) * 0.22;
    const pr = (x, y, z) => ({ x: x + z * k * Math.cos(al), y: -y - z * k * Math.sin(al) });
    const V = { f0: pr(0, 0, 0), f1: pr(A, 0, 0), f2: pr(0, B, 0), b0: pr(0, 0, P), b1: pr(A, 0, P), b2: pr(0, B, P) };
    const xs = Object.values(V).map((q) => q.x); const ys = Object.values(V).map((q) => q.y);
    const pad = 30; const sc = Math.min((VB.w - 2 * pad) / (Math.max(...xs) - Math.min(...xs)), (VB.h - 2 * pad) / (Math.max(...ys) - Math.min(...ys)));
    const ox = (VB.w - sc * (Math.max(...xs) - Math.min(...xs))) / 2; const oy = (VB.h - sc * (Math.max(...ys) - Math.min(...ys))) / 2;
    const p = {}; Object.keys(V).forEach((n) => { p[n] = { x: ox + (V[n].x - Math.min(...xs)) * sc, y: oy + (V[n].y - Math.min(...ys)) * sc }; });
    const L = (P1, P2, dash = false) => `<line x1="${P1.x.toFixed(1)}" y1="${P1.y.toFixed(1)}" x2="${P2.x.toFixed(1)}" y2="${P2.y.toFixed(1)}" stroke="#1f2937" stroke-width="1.7" stroke-linecap="round"${dash ? ' stroke-dasharray="5 4"' : ''}/>`;
    let g = '';
    // arêtes cachées : arrière-bas et fuyante du sommet arrière-bas-gauche
    g += L(p.b0, p.b1, true) + L(p.f0, p.b0, true) + L(p.b0, p.b2, true);
    // triangle avant, triangle arrière visible, fuyantes visibles
    g += L(p.f0, p.f1) + L(p.f1, p.f2) + L(p.f2, p.f0) + L(p.b1, p.b2) + L(p.f1, p.b1) + L(p.f2, p.b2);
    // codage de l'angle droit (petit carré en f0)
    const q = 9; g += `<path d="M ${(p.f0.x + q).toFixed(1)} ${p.f0.y.toFixed(1)} L ${(p.f0.x + q).toFixed(1)} ${(p.f0.y - q).toFixed(1)} L ${p.f0.x.toFixed(1)} ${(p.f0.y - q).toFixed(1)}" fill="none" stroke="#1f2937" stroke-width="1.2"/>`;
    // cotes : a sous la base, b à gauche du côté vertical, h le long de la fuyante du bas droit
    g += COTE((p.f0.x + p.f1.x) / 2, p.f0.y + 16, `${S.a} ${S.u}`);
    g += COTE(p.f0.x - 8, (p.f0.y + p.f2.y) / 2 + 4, `${S.b} ${S.u}`, 'end');
    g += COTE((p.f1.x + p.b1.x) / 2 + 20, (p.f1.y + p.b1.y) / 2 + 12, `${S.h} ${S.u}`);
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-20 0 ${VB.w + 30} ${VB.h}" width="${VB.w + 30}" height="${VB.h}" ${dataAttrs(S)} data-v="${S.a}${S.b}${S.h}">${g}</svg>`;
};

const figure = (S) => {
    if (S.type === 'cube') {
        const { svg } = paveNomme({ L: 3, H: 3, P: 3 }, { vb: VB, labels: false, coter: { L: `${S.c} ${S.u}` }, salt: S.c });
        return avecData(cadreLarge(svg, VB), S);
    }
    if (S.type === 'pave') {
        // proportions de l'image bornées (un pavé 10 × 2 × 2 reste lisible)
        const m = Math.max(S.L, S.l, S.h); const f = (x) => 1.2 + (2.8 * x) / m;
        const { fig, pts } = paveNomme({ L: f(S.L), H: f(S.h), P: f(S.l) }, { vb: VB, labels: false, coter: { L: `${S.L} ${S.u}`, H: `${S.h} ${S.u}` }, salt: S.L * 100 + S.l * 10 + S.h });
        // profondeur : cote à droite de la fuyante B → C (celle du moteur tombait sur l'arête cachée)
        fig.brut(COTE((pts.B.x + pts.C.x) / 2 + 20, (pts.B.y + pts.C.y) / 2 + 10, `${S.l} ${S.u}`));
        return avecData(cadreLarge(fig.render(), VB), S);
    }
    if (S.type === 'cylindre') {
        const vb = { w: 190, h: 200 };
        const { fig, pts } = cylindreDebout({ r: 1.2 + S.r * 0.25, h: 1.5 + S.h * 0.3, vb, coter: { h: `${S.h} ${S.u}` }, salt: S.r * 10 + S.h });
        // rayon : segment du centre du disque du haut vers la droite, cote SOUS le segment (dans le cylindre)
        const rx = pts.gR.x - pts.Cb.x;
        fig.brut(`<line x1="${pts.Ch.x.toFixed(1)}" y1="${pts.Ch.y.toFixed(1)}" x2="${(pts.Ch.x + rx).toFixed(1)}" y2="${pts.Ch.y.toFixed(1)}" stroke="#b45309" stroke-width="1.4"/>`);
        fig.brut(`<circle cx="${pts.Ch.x.toFixed(1)}" cy="${pts.Ch.y.toFixed(1)}" r="2" fill="#b45309"/>`);
        fig.brut(COTE(pts.Ch.x + rx / 2, pts.Ch.y - rx / 4 - 7, `${S.r} ${S.u}`));      // au-dessus du disque du haut (ry = rx / 4)
        return avecData(cadreLarge(fig.render(), vb, 20), S);
    }
    if (S.base === 'triangle') return prismeRectangle(S);
    // prisme à base quelconque : l'aire de la base est donnée en légende, la hauteur cotée sur la fuyante
    const { fig, pts } = prismeTri({ b: 3.4, ht: 2.4, prof: 2 + Math.min(S.h, 10) * 0.25, vb: VB, salt: S.h * 100 + S.B });
    fig.brut(COTE((pts.f1.x + pts.b1.x) / 2 + 22, (pts.f1.y + pts.b1.y) / 2 + 12, `${S.h} ${S.u}`));
    fig.brut(`<text x="${(VB.w / 2).toFixed(1)}" y="${(VB.h - 4).toFixed(1)}" font-family="sans-serif" font-size="12" text-anchor="middle" fill="#b45309">aire de la base : ${S.B} ${S.u}²</text>`);
    return avecData(cadreLarge(fig.render(), VB, 14), S);
};

const enonceFigure = (S) => {
    if (S.type === 'cube') return `<p>Voici un cube d'arête ${S.c} ${S.u}.</p>`;
    if (S.type === 'pave') return `<p>Voici un pavé de dimensions ${S.L} ${S.u}, ${S.l} ${S.u} et ${S.h} ${S.u}.</p>`;
    if (S.type === 'cylindre') return `<p>Voici un cylindre de rayon ${S.r} ${S.u} et de hauteur ${S.h} ${S.u}.</p>`;
    return S.base === 'aire'
        ? `<p>Voici un prisme droit de hauteur ${S.h} ${S.u}, dont la base a pour aire ${S.B} ${S.u}².</p>`
        : `<p>Voici un prisme droit de hauteur ${S.h} ${S.u}, dont la base est un triangle rectangle de côtés ${S.a} ${S.u} et ${S.b} ${S.u} autour de l'angle droit.</p>`;
};
const signatureDe = (S) => `${S.type}-${S.u}-${dataAttrs(S).match(/data-dims="([^"]+)"/)[1]}`;

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refF = { v: null }; const refR = { v: null }; const refS = { v: null }; const refQ = { v: null }; const refD = { v: null };

// ─── Carte 1a : la bonne formule (QCM) ─────────────────────────────────────

// Trois questions (le seul « quelle formule pour ce solide ? » n'avait que 4 tirages) : la formule d'un
// solide, le solide d'une formule, et l'intrus (une formule fausse parmi trois justes, QCM negated, conv. 1039).
const NOM_COURT = { cube: 'un cube', pave: 'un pavé', prisme: 'un prisme droit', cylindre: 'un cylindre' };
const construireFormuleQcm = () => {
    const cas = randomChoice(['formule', 'solide', 'intrus']);
    // « quel solide ? » : seules les formules propres à UN solide (c × c × c, π r r h) — « aire de la base × h »
    // vaut pour les quatre, et L × l × h pour le cube aussi (signalement prof, 2026-09-09).
    const type = cas === 'solide' ? randomChoice(['cube', 'cylindre']) : randomChoice(SOLIDES); const F = FORMULES[type];
    let payload;
    if (cas === 'formule') {
        payload = makeQcm(shuffle([F.juste, ...F.fausses]), F.juste, `<p>Quelle formule donne le volume d'${NOM_SOLIDE[type]} ?</p>`, opts(L_FORMULE));
    } else if (cas === 'solide') {
        const bonne = NOM_COURT[type];
        payload = makeQcm(shuffle(SOLIDES.map((t) => NOM_COURT[t])), bonne, `<p>Quel solide a pour volume ${F.juste} ?</p>`, opts(L_FORMULE));
    } else {
        // intrus : jamais une formule en ÷ 3 (vraie pour la pyramide et le cône, objectifs 4e ; signalement prof 2026-09-09)
        const fausse = randomChoice(F.fausses.filter((f) => !f.includes('÷ 3')));
        const autres = shuffle(SOLIDES.filter((t) => t !== type)).slice(0, 2).map((t) => FORMULES[t].juste);
        payload = makeQcm(shuffle([fausse, F.juste, ...autres]), fausse, `<p>Une seule de ces formules de volume est fausse. Laquelle ?</p>`, opts(L_FORMULE));
        payload.contenu = { ...payload.contenu, negated: true };
        payload._meta.params = { cas, type, fausse, signature: `${cas}-${type}-${fausse}` };
        payload.contenu.sousType = cas;
        return payload;
    }
    payload.contenu = { ...payload.contenu, sousType: cas };
    payload._meta.params = { cas, type, signature: `${cas}-${type}` };
    // Seule la question « quelle formule donne le volume ? » nomme ses choix faux.
    if (cas === 'formule') {
        const erreurs = F.fausses.filter((f) => CODES_FORMULE[f]).map((f) => ({ code: CODES_FORMULE[f], valeur: f }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    }
    return payload;
};
const genFormuleQcm = () => antiRepeat(construireFormuleQcm, refF);

// ─── Carte 1b : relier solide (avec ses dimensions) ↔ calcul numérique de son volume ─
// (Revue 2026-09-09, prof : « aire de la base × h » convient aux quatre solides, « L × l × h » au cube :
// un relier solide ↔ formule littérale avait plusieurs cibles justes. Ici chaque cible est un calcul propre
// à une source ; l'élève UTILISE la formule.)
const calculDe = (S) => {
    if (S.type === 'cube') return `${S.c} × ${S.c} × ${S.c}`;
    if (S.type === 'pave') return `${S.L} × ${S.l} × ${S.h}`;
    if (S.type === 'cylindre') return `π × ${S.r} × ${S.r} × ${S.h}`;
    return S.base === 'aire' ? `${S.B} × ${S.h}` : `(${S.a} × ${S.b} ÷ 2) × ${S.h}`;
};
const sourceDe = (S) => {
    if (S.type === 'cube') return `Cube d'arête ${S.c} ${S.u}`;
    if (S.type === 'pave') return `Pavé de dimensions ${S.L} ${S.u}, ${S.l} ${S.u} et ${S.h} ${S.u}`;
    if (S.type === 'cylindre') return `Cylindre de rayon ${S.r} ${S.u} et de hauteur ${S.h} ${S.u}`;
    return S.base === 'aire' ? `Prisme droit de hauteur ${S.h} ${S.u}, base d'aire ${S.B} ${S.u}²` : `Prisme droit de hauteur ${S.h} ${S.u}, base triangle rectangle de côtés ${S.a} ${S.u} et ${S.b} ${S.u}`;
};
const leurreCalcul = (S) => {
    if (S.type === 'cube') return [`${S.c} × ${S.c}`, `${S.c} × 3`, `6 × ${S.c} × ${S.c}`];
    if (S.type === 'pave') return [`${S.L} × ${S.l}`, `${S.L} + ${S.l} + ${S.h}`];
    if (S.type === 'cylindre') return [`2 × π × ${S.r} × ${S.h}`, `π × ${S.r} × ${S.h}`];
    return S.base === 'aire' ? [`${S.B} + ${S.h}`, `${S.B} × ${S.h} ÷ 3`] : [`${S.a} × ${S.b} × ${S.h}`, `(${S.a} + ${S.b}) × ${S.h}`];
};
// L'erreur que chaque leurre de leurreCalcul incarne, dans le même ordre (π × r × h : le carré du rayon ignoré).
const codesLeurres = (S) => {
    if (S.type === 'cube') return ['aire_pour_volume', 'base_fois_exposant', 'aire_pour_volume'];
    if (S.type === 'pave') return ['aire_pour_volume', 'addition_pour_produit'];
    if (S.type === 'cylindre') return ['perimetre_pour_aire', 'exposant_ignore'];
    return S.base === 'aire' ? ['addition_pour_produit', 'tiers_abusif'] : ['triangle_sans_le_demi', 'perimetre_pour_aire'];
};

const construireRelier = () => {
    for (let essai = 0; essai < 60; essai++) {
        const u = randomChoice(UNITES);
        const types = shuffle(SOLIDES).slice(0, 3);
        const solides = types.map((t) => ({ ...tirerSolide(t), u }));
        const pairs = solides.map((S) => ({ source: sourceDe(S), target: calculDe(S) }));
        const cibles = pairs.map((q) => q.target);
        const leurres = shuffle(solides.flatMap((S) => leurreCalcul(S).map((t, k) => ({ target: t, code: codesLeurres(S)[k] })))).filter((l) => !cibles.includes(l.target)).slice(0, 2);
        if (new Set([...cibles, ...leurres.map((l) => l.target)]).size !== 5) continue;
        // Revue 2026-09-09 (A1) : aucune coïncidence numérique entre les cinq cibles (un leurre ou le calcul d'un
        // autre solide ne doit jamais valoir le volume d'une source). Le symbole π reste affiché tel quel.
        const valeur = (e) => `${e.includes('π') ? 'π' : ''}${Function(`return (${e.replace(/π ×/g, '').replace(/×/g, '*').replace(/÷/g, '/')})`)()}`;
        if (new Set([...cibles, ...leurres.map((l) => l.target)].map(valeur)).size !== 5) continue;
        const payload = makeRelier(pairs, '<p>Relie chaque solide au calcul de son volume.</p>', { ...opts(L_FORMULE), cibles_leurres: leurres });
        payload._meta.params = { types, solides, signature: cibles.slice().sort().join('|') };
        // Une cible que deux erreurs différentes produisent (cube d'arête 3 : « 3 × 3 ») reste sans code.
        const codesDe = (t) => new Set(solides.flatMap((S) => leurreCalcul(S).flatMap((x, k) => (x === t ? [codesLeurres(S)[k]] : []))));
        payload.contenu = { ...payload.contenu, cibles_leurres: payload.contenu.cibles_leurres.map((l) => (codesDe(l.target).size > 1 ? { target: l.target } : l)) };
        return payload;
    }
    throw new Error('gen_1117 : relier introuvable');
};
const genRelier = () => antiRepeat(construireRelier, refR);

// ─── Carte 2a : calculer le volume (saisie sur figure) ─────────────────────

const construireCalculSaisie = () => {
    const S = tirerSolide();
    const question = S.type === 'cylindre'
        ? `<p>Quel est son volume, en ${S.u}³ ? Donne le nombre devant π.</p>`
        : `<p>Quel est son volume, en ${S.u}³ ?</p>`;
    const payload = makeSaisie(String(S.V), enonceFigure(S) + question, { ...opts(L_CALCUL), suffixe: S.type === 'cylindre' ? `π ${S.u}³` : `${S.u}³`, elements_additionnels: imgElement(figure(S), 'medium') });
    payload.contenu = { ...payload.contenu, sousType: S.type };
    payload._meta.params = { S, signature: signatureDe(S) };
    const erreurs = erreursSures(erreursVolume(S), S.V).map((e) => ({ code: e.code, valeur: String(e.v) }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genCalculSaisie = () => antiRepeat(construireCalculSaisie, refS);

// ─── Carte 2b : calculer le volume (QCM à leurres de fausses formules) ──────

const leurresDe = (S) => [...leurresPropres(S), { v: 10 * S.V, role: 'dix_fois' }];   // × 10 : erreur d'unité (revue 2026-09-09)
const leurresPropres = (S) => {
    if (S.type === 'cube') return [{ v: S.c * 3, role: 'fois_3' }, { v: S.c * S.c, role: 'aire_face' }, { v: 6 * S.c * S.c, role: 'aire_totale' }, { v: S.c * S.c * 2, role: 'deux_faces' }, { v: 2 * S.V, role: 'double' }];
    if (S.type === 'pave') return [{ v: S.L * S.l, role: 'aire_base' }, { v: S.L + S.l + S.h, role: 'somme' }, { v: 2 * S.V, role: 'double' }, { v: S.V / 3, role: 'tiers' }, { v: S.L * S.l + S.h, role: 'aire_plus_h' }];
    if (S.type === 'prisme') {
        return S.base === 'aire'
            ? [{ v: S.B + S.h, role: 'somme' }, { v: S.V / 3, role: 'tiers' }, { v: 2 * S.V, role: 'double' }, { v: S.V / 2, role: 'moitie' }]
            : [{ v: S.a * S.b * S.h, role: 'sans_demi' }, { v: (S.a + S.b) * S.h, role: 'perimetre' }, { v: S.B, role: 'aire_base' }, { v: S.V / 3, role: 'tiers' }];
    }
    return [{ v: 2 * S.r * S.h, role: 'aire_laterale' }, { v: S.r * S.h, role: 'sans_carre' }, { v: S.k / 3, role: 'cone' }, { v: 2 * S.k, role: 'double' }];
};

const construireCalculQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const S = tirerSolide();
        const cands = [];
        shuffle(leurresDe(S)).forEach((l) => { if (Number.isInteger(l.v) && l.v > 0 && l.v !== S.V && !cands.some((m) => m.v === l.v)) cands.push(l); });
        // Revue 2026-09-09 (A2) : nombre de leurres SOUS la bonne réponse tiré entre 0 et 3 (elle n'était jamais
        // la plus petite option : les fausses formules donnent presque toutes moins que le volume).
        const nBas = randInt(0, 3); const leurres = [];
        cands.filter((l) => l.v < S.V).forEach((l) => { if (leurres.length < nBas) leurres.push(l); });
        cands.filter((l) => l.v > S.V).forEach((l) => { if (leurres.length < 3) leurres.push(l); });
        cands.forEach((l) => { if (leurres.length < 3 && !leurres.includes(l)) leurres.push(l); });
        if (leurres.length < 3) continue;
        const aff = (v) => (S.type === 'cylindre' ? `${v}π ${S.u}³` : `${v} ${S.u}³`);
        const options = shuffle([S.V, ...leurres.map((l) => l.v)].map(aff));
        const payload = makeQcm(options, aff(S.V), enonceFigure(S) + '<p>Quel est son volume ?</p>', { ...opts(L_CALCUL), elements_additionnels: imgElement(figure(S), 'medium') });
        payload.contenu = { ...payload.contenu, sousType: S.type };
        payload._meta.params = { S, roles: leurres.map((l) => l.role), signature: signatureDe(S) };
        const erreurs = erreursSures(erreursVolume(S), S.V).map((e) => ({ code: e.code, valeur: aff(e.v) })).filter((e) => options.includes(e.valeur));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1117 : QCM sans leurres');
};
const genCalculQcm = () => antiRepeat(construireCalculQcm, refQ);

// ─── Carte 3 : retrouver une dimension (la hauteur) ────────────────────────

// Revue 2026-09-09 (prof) : la division inverse doit rester dans les tables — aire de base ≤ 10, rayon ≤ 3,
// hauteur de 2 à 10. Les formats de calcul direct gardent leurs plages.
const construireDimension = () => {
    const type = randomChoice(['pave', 'prisme', 'cylindre']); const u = randomChoice(UNITES); const h = randInt(2, 10);
    let S; let texte;
    if (type === 'pave') {
        const L = randInt(2, 5); const l = randInt(1, 2); const B = L * l;
        S = { type, u, L, l, h, B, V: B * h };
        texte = `<p>Un pavé a pour volume ${S.V} ${u}³. Sa base mesure ${L} ${u} sur ${l} ${u}.</p><p>Quelle est sa hauteur, en ${u} ?</p>`;
    } else if (type === 'prisme') {
        const B = randInt(5, 10);
        S = { type, u, base: 'aire', B, h, V: B * h };
        texte = `<p>Un prisme droit a pour volume ${S.V} ${u}³. Sa base a pour aire ${B} ${u}².</p><p>Quelle est sa hauteur, en ${u} ?</p>`;
    } else {
        const r = randInt(2, 3);                                          // revue 2026-09-09 (A8) : r = 1 rendait la division triviale
        S = { type, u, r, h, k: r * r * h, V: r * r * h };
        texte = `<p>Un cylindre a pour volume ${S.k}π ${u}³. Son rayon mesure ${r} ${u}.</p><p>Quelle est sa hauteur, en ${u} ?</p>`;
    }
    const payload = makeSaisie(String(h), texte, { ...opts(L_DIMENSION), suffixe: u });
    payload.contenu = { ...payload.contenu, sousType: type };
    payload._meta.params = { S, signature: `dim-${signatureDe(S)}` };
    return payload;
};
const genDimension = () => antiRepeat(construireDimension, refD);

// ─── Carte 4 : vrai ou faux ? (calculs) ────────────────────────────────────

let dernieresLignesVf1117 = [];

const genVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const candidates = shuffle(SOLIDES).map((type) => {
            const S = tirerSolide(type);
            const faux = shuffle(leurresPropres(S)).find((l) => Number.isInteger(l.v) && l.v > 0 && l.v !== S.V);   // sans le × 10 (revue 2026-09-09)
            const vrai = Math.random() < 0.5 || !faux;
            const val = vrai ? S.V : faux.v;
            const desc = S.type === 'cube' ? `Le volume d'un cube d'arête ${S.c} ${S.u} est ${val} ${S.u}³.`
                : S.type === 'pave' ? `Le volume d'un pavé de dimensions ${S.L} ${S.u}, ${S.l} ${S.u} et ${S.h} ${S.u} est ${val} ${S.u}³.`
                : S.type === 'cylindre' ? `Le volume d'un cylindre de rayon ${S.r} ${S.u} et de hauteur ${S.h} ${S.u} est ${val}π ${S.u}³.`
                : S.base === 'aire' ? `Le volume d'un prisme droit de hauteur ${S.h} ${S.u} et de base d'aire ${S.B} ${S.u}² est ${val} ${S.u}³.`
                : `Le volume d'un prisme droit de hauteur ${S.h} ${S.u}, à base triangle rectangle de côtés ${S.a} ${S.u} et ${S.b} ${S.u}, est ${val} ${S.u}³.`;
            // Code de la valeur fausse, si elle est une erreur nommée sans ambiguïté.
            const code = vrai ? null : (erreursSures(erreursVolume(S), S.V).find((e) => e.v === val) || {}).code;
            return { l: desc, v: vrai, f: type, code };
        });
        const nbFaux = candidates.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        const lignes = shuffle(candidates);
        if (essai > 120) dernieresLignesVf1117 = [];
        if (lignes.some((it) => dernieresLignesVf1117.includes(it.l))) continue;
        dernieresLignesVf1117 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(L_VF),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { lignes: lignes.map((it) => it.l) };
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1117 : V/F sans tirage après 160 essais');
};

// ─── Carte 5 : formules, vrai ou faux ? (pur rappel) ───────────────────────

const POOL_RAPPEL = [
    { l: 'Le volume d\'un cube d\'arête 3 cm est 3 × 3 × 3 cm³, soit 27 cm³.', v: true, f: 'cube' },
    { l: 'Le volume d\'un cube d\'arête 3 cm est 3 × 3 cm³, soit 9 cm³.', v: false, f: 'cube', code: 'aire_pour_volume' },
    { l: 'Le volume d\'un cube d\'arête 3 cm est 3 + 3 + 3 cm³, soit 9 cm³.', v: false, f: 'cube' },
    { l: 'Le volume d\'un pavé, c\'est longueur × largeur × hauteur.', v: true, f: 'pave' },
    { l: 'Le volume d\'un pavé, c\'est longueur × largeur.', v: false, f: 'pave', code: 'aire_de_base_pour_volume' },
    { l: 'Le volume d\'un prisme droit, c\'est l\'aire de sa base multipliée par sa hauteur.', v: true, f: 'prisme' },
    { l: 'Le volume d\'un prisme droit, c\'est le périmètre de sa base multiplié par sa hauteur.', v: false, f: 'prisme', code: 'perimetre_de_base_pour_volume' },
    { l: 'Le volume d\'un cylindre, c\'est l\'aire de son disque de base multipliée par sa hauteur.', v: true, f: 'cylindre' },
    { l: 'Le volume d\'un cylindre, c\'est 2 × π × rayon × hauteur.', v: false, f: 'cylindre', code: 'perimetre_de_base_pour_volume' },
    { l: 'L\'aire d\'un disque, c\'est π × rayon × rayon.', v: true, f: 'disque' },
    { l: 'L\'aire d\'un disque, c\'est 2 × π × rayon.', v: false, f: 'disque', code: 'perimetre_du_disque_pour_aire' },
    { l: 'Un volume s\'exprime en cm³, en dm³ ou en m³.', v: true, f: 'unite' },
    { l: 'Un volume s\'exprime en cm².', v: false, f: 'unite', code: 'unite_confondue' },
    { l: 'Le pavé et le cylindre ont la même formule : aire de la base × hauteur.', v: true, f: 'commun' },
    { l: 'Pour un prisme droit, la hauteur est la longueur qui joint les deux bases.', v: true, f: 'hauteur' },
    { l: 'Pour un prisme droit à base triangulaire, la hauteur du prisme est la hauteur du triangle.', v: false, f: 'hauteur' },
];

let dernieresLignesRappel1117 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1117 = [];
        if (lignes.some((it) => dernieresLignesRappel1117.includes(it.l))) continue;
        dernieresLignesRappel1117 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur les volumes, dis si elle est vraie ou fausse.</p>',
            opts(L_FORMULE),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1117 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    formule_qcm_unique: genFormuleQcm,
    formule_relier: genRelier,
    calcul_saisie: genCalculSaisie,
    calcul_qcm_unique: genCalculQcm,
    dimension_saisie: genDimension,
    vf_tableau_vf: genVf,
    formule_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM', relier: 'Relier', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'formule', label: L_FORMULE, formatsList: buildFormats('formule') },
    { id: 'calcul', label: L_CALCUL, formatsList: buildFormats('calcul') },
    { id: 'dimension', label: L_DIMENSION, formatsList: buildFormats('dimension') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre. Le choix reste proposé,
// il part sans nom.
const CODES_RETIRES = {
    // le lot 2 (04/10/2026) renomme ici perimetre_pour_aire : le retrait suit le nouveau nom
    calcul_qcm_unique: ['tiers_abusif', 'perimetre_de_base_pour_volume'],
    // relecture du 04/10/2026 : on ne garde un code que sur les cartes où le juge l'a lu
    formule_tableau_vf: ['aire_de_base_pour_volume', 'perimetre_de_base_pour_volume'],
    vf_tableau_vf: ['tiers_abusif', 'aire_de_base_pour_volume'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
